import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';

import { AxiosError } from 'axios';

import { Breadcrumbs, Error, Layout } from '@utilities';

import {
  readExternalQuestions,
  readInstantSurvey,
  readInstantSurveyType,
  readSurveyQuestions,
  readSurveyQuotaGroups,
} from '@api/instant_survey';
import { readDetails } from '@api/survey_dashboard';
import { readSurvey, readSurveyType, viewableSurveyStatuses } from '@api/surveys';
import { GATES } from '@constants';
import { mergeExternalQuestionsWithSurvey } from './utilities/helpers';

import Dashboard from './components/Dashboard';
import Page from '../../layouts/Page';
import SurveyNetworkError from '../../components/SurveyNetworkError';

export const Survey = ({ data, publicToken }) => {
  const { atlasInstantSurveyRef, instantSurveyTypeId, surveyId } = data || {};

  const [details, setDetails] = useState();
  const [insightsDocument, setInsightsDocument] = useState();
  const [insightsType, setInsightsType] = useState();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [survey, setSurvey] = useState();
  const [surveyQuestions, setSurveyQuestions] = useState();
  const [surveyType, setSurveyType] = useState();

  const dataRequestController = useRef();

  let { survey_id: paramSurveyId } = useParams();

  const id = parseInt(surveyId || paramSurveyId);

  useEffect(() => {
    getData();
    return () => dataRequestController?.current?.abort();
  }, [id]);

  const getData = async () => {
    const controller = new AbortController();
    dataRequestController.current = controller;
    const signal = controller.signal;

    try {
      const insightsSurvey = await readSurvey({ id, publicToken, signal });
      if (!viewableSurveyStatuses.includes(insightsSurvey.status)) {
        throw new AxiosError('Request failed with status code 404', 'ERR_BAD_REQUEST', null, null, {
          status: 404,
        });
      }

      setInsightsDocument(insightsSurvey);
      if (insightsSurvey.atlasInstantSurveyRef || atlasInstantSurveyRef) {
        const atlasRequests = [
          readDetails({
            publicToken,
            signal,
            surveyId: insightsSurvey.id,
          }),
          readInstantSurvey({
            id: insightsSurvey.atlasInstantSurveyRef || atlasInstantSurveyRef,
            params: { include_response_count: true },
            signal,
          }),
          readInstantSurveyType({
            id: insightsSurvey.instantSurveyTypeId || instantSurveyTypeId,
            signal,
          }),
          readExternalQuestions({
            signal: signal,
            surveySourceId: insightsSurvey.surveySourceId,
          }),
          readSurveyQuestions({
            id: insightsSurvey.atlasInstantSurveyRef || atlasInstantSurveyRef,
            signal,
          }),
          insightsSurvey.atlasSurveyRef
            ? readSurveyQuotaGroups({
                id: insightsSurvey.atlasSurveyRef,
                signal,
              }).catch(() => [])
            : Promise.resolve([]),
        ];
        const [
          detailsResponse,
          instantSurveyResponse,
          instantSurveyTypeResponse,
          externalQuestionsResponse,
          surveyQuestionsResponse,
          atlasQuotaGroups,
        ] = await Promise.all(atlasRequests);
        const quotaLimitByDefinitionRef = new Map(
          atlasQuotaGroups.map(({ id: definitionId, quotaLimit }) => [
            String(definitionId),
            quotaLimit,
          ])
        );
        const { groups, ...instantSurvey } = instantSurveyResponse;
        setDetails(detailsResponse);
        setSurvey({
          ...instantSurvey,
          ...surveyQuestionsResponse,
          // sorting groups here so it never impacts a draft survey
          groups: groups
            .map((group) => ({
              ...group,
              quotaLimit:
                quotaLimitByDefinitionRef.get(String(group.quotaGroupDefinitionRef)) ??
                group.quotaLimit,
            }))
            .sort((a, b) => a.quotaGroupRef - b.quotaGroupRef),
        });
        setSurveyQuestions(
          mergeExternalQuestionsWithSurvey({
            externalQuestions: externalQuestionsResponse,
            surveyQuestions: surveyQuestionsResponse.questions,
          })
        );
        setSurveyType(instantSurveyTypeResponse);
      } else {
        const [detailsResponse, surveyTypeResponse, externalQuestionsResponse] = await Promise.all([
          readDetails({
            publicToken,
            signal,
            surveyId: insightsSurvey.id,
          }),
          readSurveyType({
            documentId: insightsSurvey.instantSurveyTypeId || instantSurveyTypeId,
            publicToken,
            signal,
          }),
          readExternalQuestions({
            signal: signal,
            surveySourceId: insightsSurvey.surveySourceId,
          }),
        ]);
        setDetails(detailsResponse);
        setInsightsType(surveyTypeResponse);
        setSurveyQuestions(externalQuestionsResponse);
      }
      setIsDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        setLoadingError(error);
      }
    }
  };

  return (
    <>
      {!publicToken && <Breadcrumbs />}
      {loadingError &&
        (loadingError?.code === 'ERR_NETWORK' ? (
          <SurveyNetworkError />
        ) : (
          <Error status={loadingError?.response?.status} />
        ))}
      {!loadingError && (
        <>
          {isDataLoading ? (
            <Dashboard.Skeleton />
          ) : (
            <Dashboard
              details={details}
              id={id}
              insightsDocument={insightsDocument}
              insightsType={insightsType}
              publicToken={publicToken}
              survey={survey}
              surveyQuestions={surveyQuestions}
              surveyType={surveyType}
            />
          )}
        </>
      )}
    </>
  );
};

Survey.propTypes = {
  data: PropTypes.shape({
    atlasInstantSurveyRef: PropTypes.string,
    instantSurveyTypeId: PropTypes.string,
    surveyId: PropTypes.number,
  }),
  publicToken: PropTypes.string,
};

const SurveyPage = (props) => {
  const client = useStatsigClient();
  const isDashboardFullWidth = client.checkGate(GATES.SURVEY_DASHBOARD_FULL_WIDTH);

  return isDashboardFullWidth ? (
    <Page>
      <Survey {...props} />
    </Page>
  ) : (
    <Page>
      <Layout.Column>
        <Survey {...props} />
      </Layout.Column>
    </Page>
  );
};

export default SurveyPage;
